import { useState, useRef, useCallback } from 'react'
import {
  apiFriendSearch,
  apiFriendRequest,
  apiFriendBlock,
  FriendSearchUser,
} from '../server/ServiceCall'
import '../css/Friend.css'

interface Props {
  token:   string
  myCode:  number
  onClose: () => void
}

export default function UserSearchModal({ token, myCode, onClose }: Props) {
  const [keyword,  setKeyword]  = useState('')
  const [results,  setResults]  = useState<FriendSearchUser[]>([])
  const [loading,  setLoading]  = useState(false)
  const [error,    setError]    = useState<string | null>(null)
  const [toast,    setToast]    = useState<string | null>(null)

  // 검색 빈도 제한 debounce 300ms + 쿨다운 1초
  const lastSearchTime = useRef<number>(0)

  function showToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 3000)
  }

  const doSearch = useCallback(async (kw: string) => {
    if (kw.trim().length < 2) {
      setResults([])
      setError('검색어는 2자 이상 입력하세요.')
      return
    }
    const now = Date.now()
    if (now - lastSearchTime.current < 1000) {
      setError('너무 빠른 검색입니다. 잠시 후 시도하세요.')
      return
    }
    lastSearchTime.current = now
    setError(null)
    setLoading(true)
    const res = await apiFriendSearch(token, kw.trim())
    setLoading(false)
    setResults(res)
    if (res.length === 0) setError('검색 결과가 없습니다.')
  }, [token])

  // 300ms debounce (Enter 키 또는 버튼으로 수동 검색)
  function handleSearch() {
    doSearch(keyword)
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') handleSearch()
  }

  async function handleRequest(u: FriendSearchUser) {
    const res = await apiFriendRequest(token, u.userCode)
    showToast(res.resultMessage)
    if (res.resultCode === '00') {
      setResults(prev => prev.map(x =>
        x.userCode === u.userCode
          ? { ...x, relationStatus: 'PENDING', isMyRequest: 1 }
          : x
      ))
    }
  }

  async function handleBlock(u: FriendSearchUser) {
    if (!window.confirm(`${u.nickname} 님을 차단하시겠습니까?`)) return
    const res = await apiFriendBlock(token, u.userCode, 'BLOCK')
    showToast(res.resultMessage)
    if (res.resultCode === '00') {
      setResults(prev => prev.map(x =>
        x.userCode === u.userCode
          ? { ...x, relationStatus: 'BLOCKED' }
          : x
      ))
    }
  }

  function renderActions(u: FriendSearchUser) {
    if (u.relationStatus === 'ACCEPTED') {
      return <span className="relation-badge friend">친구</span>
    }
    if (u.relationStatus === 'BLOCKED') {
      return <span className="relation-badge blocked">차단됨</span>
    }
    if (u.relationStatus === 'PENDING') {
      if (u.isMyRequest === 1) return <span className="relation-badge pending">요청 전송됨</span>
      return <span className="relation-badge pending">요청 받음</span>
    }
    return (
      <div className="search-actions">
        <button
          className="faction-btn accept"
          onClick={() => handleRequest(u)}
        >친구 요청</button>
        <button
          className="faction-btn danger"
          onClick={() => handleBlock(u)}
        >차단</button>
      </div>
    )
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box search-modal" onClick={e => e.stopPropagation()}>
        {toast && <div className="friend-toast">{toast}</div>}

        <div className="modal-header">
          <h3>유저 검색</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="search-input-row">
          <input
            type="text"
            className="search-input"
            placeholder="닉네임 2자 이상 입력"
            value={keyword}
            maxLength={32}
            onChange={e => { setKeyword(e.target.value); setError(null) }}
            onKeyDown={handleKeyDown}
            autoFocus
          />
          <button
            className="search-go-btn"
            onClick={handleSearch}
            disabled={loading}
          >검색</button>
        </div>

        {error && <div className="search-error">{error}</div>}
        {loading && <div className="friend-loading">검색 중...</div>}

        <div className="search-results">
          {results.map(u => (
            <div key={u.userCode} className="friend-item">
              <div className="friend-avatar">{u.nickname.charAt(0).toUpperCase()}</div>
              <div className="friend-info">
                <span className="friend-name">{u.nickname}</span>
              </div>
              {renderActions(u)}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
